import type { StatusTone } from './StatusPill';
import type { Docket, DocketLifecycleStatus, DocketSyncStatus } from './schema';

export function formatDocketDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const SYNC_STATUS_LABELS: Record<DocketSyncStatus, string> = {
  not_started: 'Not started',
  metadata_synced: 'Metadata synced',
  syncing: 'Syncing',
  synced: 'Synced',
  failed: 'Failed',
  partial: 'Partial',
};

export function syncStatusLabel(status: DocketSyncStatus): string {
  return SYNC_STATUS_LABELS[status] ?? 'Unknown';
}

const SYNC_STATUS_TONES: Record<DocketSyncStatus, StatusTone> = {
  not_started: 'slate',
  metadata_synced: 'blue',
  syncing: 'blue',
  synced: 'emerald',
  failed: 'red',
  partial: 'amber',
};

export function syncStatusTone(status: DocketSyncStatus): StatusTone {
  return SYNC_STATUS_TONES[status] ?? 'slate';
}

export function syncStatusPulses(status: DocketSyncStatus): boolean {
  return status === 'syncing';
}

export function caseStatusTone(status: string | null | undefined): StatusTone {
  const normalized = status?.toLowerCase();
  if (normalized === 'open' || normalized === 'active') return 'emerald';
  if (normalized === 'terminated' || normalized === 'closed') return 'slate';
  if (normalized === 'pending' || normalized === 'stayed') return 'amber';
  return 'slate';
}

// Tone for the normalized cross-provider lifecycle, used for the docket header
// status pill so the colour keys off a stable value rather than raw provider text.
export function lifecycleStatusTone(status: DocketLifecycleStatus): StatusTone {
  if (status === 'open') return 'emerald';
  return 'slate';
}

export interface DocketCounts {
  entries: number;
  documents: number;
}

export function getDocketCounts(docket: Docket): DocketCounts {
  const entryDocuments = docket.entries.reduce((total, entry) => total + entry.documents.length, 0);
  return {
    entries: docket.entries.length,
    documents: entryDocuments + docket.documents.length,
  };
}

// A freshly linked docket has its metadata immediately but its entries are
// populated by a background sync that can take a few minutes. Within this
// window we show a "still syncing" hint instead of a misleading "0 entries".
export const FRESH_DOCKET_WINDOW_MS = 5 * 60 * 1000;

export function isAwaitingFirstSync(docket: Docket): boolean {
  // Entries already arrived, or this docket genuinely has none / failed to
  // sync — in those cases the real count (or the failure pill) is correct.
  if (docket.entries.length > 0) return false;
  if (docket.syncStatus === 'synced' || docket.syncStatus === 'failed') return false;

  // No sync run has started yet (just linked) — entries are still coming.
  if (!docket.lastSyncStartedAt) return true;

  const startedAt = new Date(docket.lastSyncStartedAt).getTime();
  if (Number.isNaN(startedAt)) return false;
  return Date.now() - startedAt < FRESH_DOCKET_WINDOW_MS;
}
